import { useState, useEffect, useRef, useMemo, type ChangeEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardHeader from '@mui/material/CardHeader';
import CardContent from '@mui/material/CardContent';
import TextField from '@mui/material/TextField';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import MenuItem from '@mui/material/MenuItem';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Divider from '@mui/material/Divider';
import Avatar from '@mui/material/Avatar';
import IconButton from '@mui/material/IconButton';
import Collapse from '@mui/material/Collapse';
import CircularProgress from '@mui/material/CircularProgress';
import InputLabel from '@mui/material/InputLabel';
import {
  Save,
  Business as BusinessIcon,
  Schedule,
  CalendarMonth,
  AttachMoney,
  Notifications,
  Palette,
  ContentCopy,
  Check,
  OpenInNew,
  Public,
  Phone,
  Email,
  Place,
  Link as LinkIcon,
  People,
  ChevronRight,
  ExpandMore,
  Title,
  Image as ImageIcon,
  Close,
  Upload,
  Visibility,
} from '@mui/icons-material';
import Publish from '@mui/icons-material/Publish';
import Article from '@mui/icons-material/Article';
import MailOutline from '@mui/icons-material/MailOutline';
import MuiLink from '@mui/material/Link';
import { useTranslation } from 'react-i18next';
import TimeTextField from '@/components/TimeTextField';
import i18n from '@/i18n/i18n';
import {
  queryClient,
  apiRequest,
  adminUploadRequest,
  adminBusinessQueryKey,
  fetchAdminBusinessJson,
  fetchAdminJsonStaffHours,
} from '@/lib/queryClient';
import { useToast } from '@/hooks/useToast';
import {
  useStaffListWorkingHours,
  workingHoursToStaffScheduleDays,
  isPlaceholderStaffId,
} from '@/hooks/useStaffListWorkingHours';
import { mapStaffWorkingHourApiRowsToUi } from '@/lib/adminApi';
import { useAdminStaffCatalog } from '@/hooks/useAdminPagedCatalogs';
import {
  formatCents,
  DAYS_OF_WEEK,
  getInitials,
  BOOKING_MODE_OPTIONS,
  BUSINESS_TYPE_OPTIONS,
} from '@/lib/utils';
import { isReservedSlug } from '@/lib/slugReservedWords';
import { radiusToCss, type Business, type BookingPageConfig, type Staff } from '@shared/schema';

/** Stable key when GET /staff returns duplicate `id` values for different rows. */
function staffScheduleRowKey(member: Staff, index: number) {
  return `${member.id}:${index}`;
}

const TAB_KEYS = [
  'business',
  'booking-rules',
  'working-hours',
  'payments',
  'notifications',
  'booking-page',
] as const;
type TabKey = (typeof TAB_KEYS)[number];

interface DayHours {
  dayOfWeek: number;
  isOpen: boolean;
  startTime: string;
  endTime: string;
}

interface StaffDayHours {
  dayOfWeek: number;
  isActive: boolean;
  startTime: string;
  endTime: string;
}

interface NotificationPrefs {
  adminNewBooking: boolean;
  adminCancellation: boolean;
  adminNoShow: boolean;
  staffNewBooking: boolean;
  customerConfirmation: boolean;
  customerCancellation: boolean;
  customerReminder: boolean;
  reminderLeadTimeHours: number;
  customerFollowUp: boolean;
}

const DAY_DISPLAY_ORDER = [1, 2, 3, 4, 5, 6, 0];

const defaultOpeningHours: DayHours[] = DAYS_OF_WEEK.map((_, i) => ({
  dayOfWeek: i,
  isOpen: i >= 1 && i <= 5,
  startTime: '09:00',
  endTime: '17:00',
}));

const defaultNotificationPrefs: NotificationPrefs = {
  adminNewBooking: true,
  adminCancellation: true,
  adminNoShow: false,
  staffNewBooking: true,
  customerConfirmation: true,
  customerCancellation: true,
  customerReminder: false,
  reminderLeadTimeHours: 24,
  customerFollowUp: false,
};

const TIMEZONE_IDS = [
  'Europe/London',
  'Europe/Paris',
  'Europe/Berlin',
  'Europe/Amsterdam',
  'Europe/Madrid',
  'Europe/Rome',
  'Europe/Dublin',
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'America/Toronto',
  'America/Vancouver',
  'Australia/Sydney',
  'Australia/Melbourne',
  'Pacific/Auckland',
] as const;

function timezoneLabelKey(value: string): string {
  return `settings.timezoneLabels.${value.replace(/\//g, '_')}`;
}

/** API may send FIXED/percent; MUI Select values are fixed | percent. */
function normalizeDefaultDepositType(raw: unknown): 'fixed' | 'percent' {
  const s = String(raw ?? 'fixed')
    .trim()
    .toLowerCase();
  if (s === 'percent') return 'percent';
  return 'fixed';
}

const GALLERY_SLOT_COUNT = 6;

/** Pad gallery URLs to fixed slots (matches schema max). */
function padGallerySlots(urls: string[] | undefined): string[] {
  const src = urls || [];
  return Array.from({ length: GALLERY_SLOT_COUNT }, (_, i) =>
    typeof src[i] === 'string' ? src[i] : ''
  );
}

type BusinessApi = Business & {
  bookingMode?: string | null;
  openingHours?: DayHours[] | null;
  notificationPreferences?: NotificationPrefs | null;
};

function useSettingsTab(): [TabKey, (t: TabKey) => void] {
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = useMemo(() => {
    const v = searchParams.get('tab') as TabKey | null;
    return v && TAB_KEYS.includes(v) ? v : 'business';
  }, [searchParams]);
  const setTab = (t: TabKey) => {
    setSearchParams({ tab: t });
  };
  return [tab, setTab];
}

function ImageUpload({
  value,
  onChange,
  label,
  placeholder,
  testId,
  aspectRatio = 'wide',
}: {
  value: string;
  onChange: (url: string) => void;
  label?: string;
  placeholder?: string;
  testId?: string;
  /** square = logo; wide = cover; gallery = compact slots in grid (single drop zone, no extra button row) */
  aspectRatio?: 'square' | 'wide' | 'gallery';
}) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);

  const boxH = aspectRatio === 'square' ? 96 : aspectRatio === 'gallery' ? 88 : 128;

  async function handleFileChange(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const { url } = await adminUploadRequest(formData);
      onChange(url);
      toast({ title: t('toasts.imageUploaded') });
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : t('toasts.uploadFailed');
      toast({ title: t('toasts.uploadFailed'), description: msg, variant: 'destructive' });
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }

  function openFilePicker() {
    if (!uploading) fileInputRef.current?.click();
  }

  return (
    <Stack spacing={0.75} sx={{ minWidth: 0, height: '100%' }}>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/gif,image/webp,image/svg+xml"
        hidden
        onChange={handleFileChange}
        data-testid={testId ? `input-file-${testId}` : undefined}
      />
      {value ? (
        <Box
          sx={{
            position: 'relative',
            '&:hover .remove-img': { opacity: 1 },
            '&:hover .replace-hint': { opacity: 1 },
          }}
        >
          <Box
            component="img"
            src={value}
            alt={String(label || t('common.image'))}
            data-testid={testId ? `preview-${testId}` : undefined}
            onClick={openFilePicker}
            sx={{
              objectFit: 'cover',
              borderRadius: 1,
              border: 1,
              borderColor: 'divider',
              height: boxH,
              width: aspectRatio === 'square' ? 96 : '100%',
              cursor: uploading ? 'wait' : 'pointer',
              display: 'block',
            }}
          />
          <Typography
            variant="caption"
            className="replace-hint"
            sx={{
              position: 'absolute',
              bottom: 6,
              left: '50%',
              transform: 'translateX(-50%)',
              px: 1,
              py: 0.25,
              borderRadius: 0.5,
              bgcolor: 'rgba(0,0,0,0.6)',
              color: 'common.white',
              opacity: 0,
              transition: 'opacity 0.2s',
              pointerEvents: 'none',
            }}
          >
            {t('common.replaceImage')}
          </Typography>
          {uploading && (
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: 'rgba(0,0,0,0.35)',
                borderRadius: 1,
              }}
            >
              <CircularProgress size={28} color="inherit" sx={{ color: 'common.white' }} />
            </Box>
          )}
          <IconButton
            size="small"
            className="remove-img"
            onClick={(e) => {
              e.stopPropagation();
              onChange('');
            }}
            sx={{
              position: 'absolute',
              top: 4,
              right: 4,
              bgcolor: 'rgba(0,0,0,0.55)',
              color: 'common.white',
              opacity: 0,
              transition: 'opacity 0.2s',
            }}
            aria-label={t('common.removeImage')}
          >
            <Close fontSize="small" />
          </IconButton>
        </Box>
      ) : (
        <Box
          onClick={openFilePicker}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              openFilePicker();
            }
          }}
          role="button"
          tabIndex={0}
          aria-label={String(placeholder || t('common.clickToUpload'))}
          data-testid={testId ? `upload-area-${testId}` : undefined}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: 2,
            borderStyle: 'dashed',
            borderColor: 'divider',
            borderRadius: 1,
            bgcolor: 'action.hover',
            cursor: uploading ? 'wait' : 'pointer',
            height: aspectRatio === 'gallery' ? boxH : aspectRatio === 'square' ? boxH : 112,
            minHeight: aspectRatio === 'gallery' ? boxH : undefined,
            '&:hover': { borderColor: 'primary.light', bgcolor: 'action.selected' },
          }}
        >
          <Stack alignItems="center" spacing={0.5} color="text.secondary">
            {uploading ? (
              <CircularProgress size={22} />
            ) : (
              <>
                <Upload fontSize="small" />
                <Typography variant="caption" textAlign="center" sx={{ px: 0.5 }}>
                  {placeholder || t('common.clickToUpload')}
                </Typography>
              </>
            )}
          </Stack>
        </Box>
      )}
      {value && aspectRatio !== 'gallery' && (
        <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: '100%' }}>
          {value.split('/').pop()}
        </Typography>
      )}
    </Stack>
  );
}

function NotifRow({
  label,
  description,
  checked,
  onChange,
  testId,
}: {
  label: string;
  description: string;
  checked: boolean;
  onChange: (v: boolean) => void;
  testId: string;
}) {
  return (
    <Stack
      direction="row"
      alignItems="flex-start"
      justifyContent="space-between"
      spacing={2}
      sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 2 }}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body2" fontWeight={600}>
          {label}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {description}
        </Typography>
      </Box>
      <Switch checked={checked} onChange={(_, v) => onChange(v)} data-testid={testId} />
    </Stack>
  );
}

export default function AdminSettings() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [currentTab, setCurrentTab] = useSettingsTab();

  const { data: business, isLoading } = useQuery<BusinessApi | null>({
    queryKey: adminBusinessQueryKey,
    queryFn: fetchAdminBusinessJson,
  });
  const hasBusiness = business != null;
  const { staff: staffList } = useAdminStaffCatalog();
  const { hoursByStaffIndex } = useStaffListWorkingHours(staffList);

  const [urlCopied, setUrlCopied] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    category: 'general',
    bookingMode: 'appointment',
    timezone: 'Europe/London',
    currency: 'GBP',
    address: '',
    phone: '',
    email: '',
    website: '',
    description: '',
    accentColor: '#7c3aed',
    minLeadTimeMins: 120,
    maxBookingDays: 90,
    slotIncrementMins: 15,
    cancellationPolicyText: '',
    depositEnabled: false,
    defaultDepositType: 'fixed',
    defaultDepositValue: 0,
  });

  const bookingUrl = useMemo(() => {
    const origin = typeof window !== 'undefined' ? window.location.origin : '';
    const trimmed = formData.slug?.trim();
    if (trimmed) {
      return `${origin}/book/${encodeURIComponent(trimmed)}`;
    }
    return origin ? `${origin}/` : '/';
  }, [formData.slug]);

  function copyBookingUrl() {
    navigator.clipboard.writeText(bookingUrl).then(() => {
      setUrlCopied(true);
      setTimeout(() => setUrlCopied(false), 2000);
    });
  }

  const [openingHours, setOpeningHours] = useState<DayHours[]>(defaultOpeningHours);
  const [notifPrefs, setNotifPrefs] = useState<NotificationPrefs>(defaultNotificationPrefs);
  const [staffSchedules, setStaffSchedules] = useState<Record<string, StaffDayHours[]>>({});
  const [expandedStaff, setExpandedStaff] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (isLoading) return;
    if (business) return;
    if (currentTab !== 'business') {
      setCurrentTab('business');
    }
  }, [isLoading, business, currentTab, setCurrentTab]);

  useEffect(() => {
    if (business) {
      setFormData({
        name: business.name || '',
        slug: business.slug || '',
        category: business.category || 'general',
        bookingMode: (business as BusinessApi).bookingMode || 'appointment',
        timezone: business.timezone || 'Europe/London',
        currency: business.currency || 'GBP',
        address: business.address || '',
        phone: business.phone || '',
        email: business.email || '',
        website: business.website || '',
        description: business.description || '',
        accentColor: business.accentColor || '#7c3aed',
        minLeadTimeMins: business.minLeadTimeMins ?? 120,
        maxBookingDays: business.maxBookingDays ?? 90,
        slotIncrementMins: business.slotIncrementMins ?? 15,
        cancellationPolicyText: business.cancellationPolicyText || '',
        depositEnabled: business.depositEnabled || false,
        defaultDepositType: normalizeDefaultDepositType(business.defaultDepositType),
        defaultDepositValue: business.defaultDepositValue || 0,
      });
      const oh = (business as BusinessApi).openingHours;
      if (oh && Array.isArray(oh)) setOpeningHours(oh as DayHours[]);
      const np = (business as BusinessApi).notificationPreferences;
      if (np && typeof np === 'object') setNotifPrefs({ ...defaultNotificationPrefs, ...np });
    }
  }, [business]);

  useEffect(() => {
    if (!staffList || !hoursByStaffIndex) return;
    const schedules: Record<string, StaffDayHours[]> = {};
    staffList.forEach((member, idx) => {
      const memberHours = hoursByStaffIndex[idx] ?? [];
      schedules[staffScheduleRowKey(member, idx)] = workingHoursToStaffScheduleDays(memberHours);
    });
    setStaffSchedules(schedules);
  }, [staffList, hoursByStaffIndex]);

  const defaultBrandingCfg: BookingPageConfig = {
    pageTitle: '',
    tagline: '',
    themeMode: 'light',
    primaryColor: '#0EA5E9',
    radius: 'xl',
    logoUrl: '',
    coverImageUrl: '',
    galleryUrls: [],
    showPrices: true,
    showDurations: true,
    showStaff: true,
    showAddress: true,
    showPhone: true,
    poweredByEposNow: true,
  };
  const [brandingCfg, setBrandingCfg] = useState<BookingPageConfig>({ ...defaultBrandingCfg });

  useEffect(() => {
    if (business) {
      const ex = (business.bookingPageDraftConfig ||
        business.bookingPageConfig ||
        {}) as Partial<BookingPageConfig>;
      setBrandingCfg({
        pageTitle:
          ex.pageTitle || i18n.t('settings.bookingPage.bookWithDefault', { name: business.name }),
        tagline: ex.tagline || '',
        themeMode: ex.themeMode || 'light',
        primaryColor: ex.primaryColor || '#0EA5E9',
        radius: ex.radius || 'xl',
        logoUrl: ex.logoUrl || '',
        coverImageUrl: ex.coverImageUrl || '',
        galleryUrls: padGallerySlots(ex.galleryUrls),
        showPrices: ex.showPrices ?? true,
        showDurations: ex.showDurations ?? true,
        showStaff: ex.showStaff ?? true,
        showAddress: ex.showAddress ?? true,
        showPhone: ex.showPhone ?? true,
        poweredByEposNow: ex.poweredByEposNow ?? true,
      });
    }
  }, [business]);

  function updateBrandingCfg(updates: Partial<BookingPageConfig>) {
    setBrandingCfg((prev) => ({ ...prev, ...updates }));
  }

  const brandingToggleItems = useMemo(
    () =>
      [
        { key: 'showPrices' as const, labelKey: 'settings.bookingPage.showPrices' },
        { key: 'showDurations' as const, labelKey: 'settings.bookingPage.showDurations' },
        { key: 'showStaff' as const, labelKey: 'settings.bookingPage.showStaff' },
        { key: 'showAddress' as const, labelKey: 'settings.bookingPage.showAddress' },
        { key: 'showPhone' as const, labelKey: 'settings.bookingPage.showPhone' },
        { key: 'poweredByEposNow' as const, labelKey: 'common.poweredByEposNow' },
      ] as const,
    []
  );

  const bookingPageThemeModeLabel = useMemo(() => {
    switch (brandingCfg.themeMode) {
      case 'light':
        return t('settings.bookingPage.themeLight');
      case 'dark':
        return t('settings.bookingPage.themeDark');
      default:
        return t('settings.bookingPage.themeAuto');
    }
  }, [brandingCfg.themeMode, t]);

  const gallerySlots = useMemo(
    () => padGallerySlots(brandingCfg.galleryUrls),
    [brandingCfg.galleryUrls]
  );

  const depositPreview = useMemo(() => {
    if (!formData.depositEnabled) return t('settings.payments.noDeposit');
    if (formData.defaultDepositType === 'percent') {
      return t('settings.payments.depositPercentPreview', { value: formData.defaultDepositValue });
    }
    return formatCents(formData.defaultDepositValue, formData.currency);
  }, [
    formData.depositEnabled,
    formData.defaultDepositType,
    formData.defaultDepositValue,
    formData.currency,
    t,
  ]);

  const updateMutation = useMutation({
    mutationFn: (
      data: typeof formData & {
        openingHours: DayHours[];
        notificationPreferences: NotificationPrefs;
      }
    ) => apiRequest('PATCH', '/api/admin/business', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminBusinessQueryKey });
      toast({ title: t('toasts.settingsSaved') });
    },
    onError: (err: Error) =>
      toast({ title: t('common.error'), description: err.message, variant: 'destructive' }),
  });

  /** OpenAPI: POST when no business exists yet (GET returned 404). Body matches CreateBusinessBody. */
  const createBusinessMutation = useMutation({
    mutationFn: async (body: Record<string, unknown>) => {
      const res = await apiRequest('POST', '/api/admin/business', body);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminBusinessQueryKey });
      toast({ title: t('toasts.businessRegistered') });
    },
    onError: (err: Error) =>
      toast({ title: t('common.error'), description: err.message, variant: 'destructive' }),
  });

  const saveBrandingMutation = useMutation({
    mutationFn: () =>
      apiRequest('PATCH', '/api/admin/business/branding/draft', {
        ...brandingCfg,
        galleryUrls: padGallerySlots(brandingCfg.galleryUrls),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminBusinessQueryKey });
      queryClient.invalidateQueries({ queryKey: ['public-config'] });
      toast({
        title: t('toasts.bookingPageSaved'),
        description: t('toasts.bookingPageSavedDescription'),
      });
    },
    onError: (err: Error) =>
      toast({ title: t('toasts.saveFailed'), description: err.message, variant: 'destructive' }),
  });

  const publishBrandingMutation = useMutation({
    mutationFn: () => apiRequest('POST', '/api/admin/business/branding/publish'),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminBusinessQueryKey });
      queryClient.invalidateQueries({ queryKey: ['public-config'] });
      toast({ title: t('toasts.brandingPublished') });
    },
    onError: (err: Error) =>
      toast({ title: t('toasts.saveFailed'), description: err.message, variant: 'destructive' }),
  });

  const savePending = updateMutation.isPending || createBusinessMutation.isPending;

  const slugReservedError = useMemo(
    () =>
      formData.slug && isReservedSlug(formData.slug) ? t('settings.business.slugReserved') : '',
    [formData.slug, t]
  );

  function handleSave() {
    if (formData.slug && isReservedSlug(formData.slug)) {
      toast({
        title: t('common.error'),
        description: t('settings.business.slugReserved'),
        variant: 'destructive',
      });
      return;
    }
    if (hasBusiness) {
      updateMutation.mutate({
        ...formData,
        openingHours,
        notificationPreferences: notifPrefs,
      });
      return;
    }
    const name = formData.name.trim();
    const slug = formData.slug.trim();
    if (!name || !slug) {
      toast({
        title: t('common.error'),
        description: t('settings.business.registerRequiredFields'),
        variant: 'destructive',
      });
      return;
    }
    createBusinessMutation.mutate({
      name,
      slug,
      timezone: formData.timezone,
      openingHours: openingHours.map((d) => ({
        dayOfWeek: d.dayOfWeek,
        isOpen: d.isOpen,
        startTime: d.startTime,
        endTime: d.endTime,
      })),
    });
  }

  function updateDayHours(dayOfWeek: number, patch: Partial<DayHours>) {
    setOpeningHours((prev) =>
      prev.map((d) => (d.dayOfWeek === dayOfWeek ? { ...d, ...patch } : d))
    );
  }

  /** `rowKey` matches `staffScheduleRowKey(member, idx)` — not raw `member.id` when ids can repeat. */
  function updateStaffDayHours(rowKey: string, dayOfWeek: number, patch: Partial<StaffDayHours>) {
    setStaffSchedules((prev) => ({
      ...prev,
      [rowKey]: (prev[rowKey] ?? []).map((d) =>
        d.dayOfWeek === dayOfWeek ? { ...d, ...patch } : d
      ),
    }));
  }

  function toggleExpand(staffId: string) {
    setExpandedStaff((prev) => {
      const next = new Set(prev);
      if (next.has(staffId)) next.delete(staffId);
      else next.add(staffId);
      return next;
    });
  }

  const [savingStaff, setSavingStaff] = useState<Record<string, boolean>>({});

  async function saveStaffHours(rowKey: string, staffIdForApi: string) {
    if (isPlaceholderStaffId(staffIdForApi)) {
      toast({
        title: t('common.error'),
        description: t('settings.hours.placeholderStaffSave'),
        variant: 'destructive',
      });
      return;
    }
    setSavingStaff((prev) => ({ ...prev, [rowKey]: true }));
    try {
      await apiRequest('PATCH', `/api/admin/staff/${staffIdForApi}/hours`, {
        workingHours: staffSchedules[rowKey] ?? [],
      });
      const raw = await fetchAdminJsonStaffHours(staffIdForApi);
      const idx = staffList.findIndex((m, i) => staffScheduleRowKey(m, i) === rowKey);
      const memberHours = mapStaffWorkingHourApiRowsToUi(
        staffIdForApi,
        idx >= 0 ? idx : 0,
        raw,
      );
      setStaffSchedules((prev) => ({
        ...prev,
        [rowKey]: workingHoursToStaffScheduleDays(memberHours),
      }));
      await queryClient.invalidateQueries({ queryKey: ['/api/admin/staff'] });
      toast({ title: t('toasts.scheduleSaved') });
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : t('common.error');
      toast({ title: t('common.error'), description: msg, variant: 'destructive' });
    } finally {
      setSavingStaff((prev) => ({ ...prev, [rowKey]: false }));
    }
  }

  if (isLoading) {
    return (
      <Box sx={{ p: 3, maxWidth: 900, mx: 'auto' }}>
        <Stack spacing={2}>
          <Skeleton variant="rounded" height={32} width={192} />
          <Skeleton variant="rounded" height={48} />
          <Skeleton variant="rounded" height={320} />
        </Stack>
      </Box>
    );
  }

  const tabIndex = TAB_KEYS.indexOf(currentTab);

  return (
    <Box sx={{ p: 3, maxWidth: 900, mx: 'auto' }}>
      <Stack
        direction="row"
        alignItems="flex-start"
        justifyContent="space-between"
        flexWrap="wrap"
        gap={2}
        sx={{ mb: 2 }}
      >
        <Box>
          <Typography variant="h5" fontWeight={700} data-testid="text-settings-title">
            {t('settings.title')}
          </Typography>
          <Typography color="text.secondary" variant="body2">
            {t('settings.subtitle')}
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<Save />}
          onClick={handleSave}
          disabled={savePending || Boolean(slugReservedError)}
          data-testid="button-save-settings"
        >
          {savePending ? t('common.saving') : t('settings.saveChanges')}
        </Button>
      </Stack>

      <Tabs
        value={tabIndex >= 0 ? tabIndex : 0}
        onChange={(_, v) => setCurrentTab(TAB_KEYS[v as number])}
        variant="scrollable"
        scrollButtons="auto"
        sx={{
          borderBottom: 1,
          borderColor: 'divider',
          mb: 3,
          '& .MuiTab-root .MuiSvgIcon-root': {
            color: 'text.secondary',
            fontSize: '1.25rem',
          },
          '& .MuiTab-root.Mui-selected .MuiSvgIcon-root': {
            color: 'primary.main',
          },
        }}
        data-testid="settings-tabs"
      >
        <Tab
          icon={<BusinessIcon />}
          iconPosition="start"
          label={t('settings.tabs.business')}
          data-testid="tab-business"
        />
        <Tab
          icon={<Schedule />}
          iconPosition="start"
          label={t('settings.tabs.rules')}
          disabled={!hasBusiness}
          data-testid="tab-booking-rules"
        />
        <Tab
          icon={<CalendarMonth />}
          iconPosition="start"
          label={t('settings.tabs.hours')}
          disabled={!hasBusiness}
          data-testid="tab-working-hours"
        />
        <Tab
          icon={<AttachMoney />}
          iconPosition="start"
          label={t('settings.tabs.pay')}
          disabled={!hasBusiness}
          data-testid="tab-payments"
        />
        <Tab
          icon={<Notifications />}
          iconPosition="start"
          label={t('settings.tabs.notifs')}
          disabled={!hasBusiness}
          data-testid="tab-notifications"
        />
        <Tab
          icon={<Palette />}
          iconPosition="start"
          label={t('settings.tabs.bookingPage')}
          disabled={!hasBusiness}
          data-testid="tab-booking-page"
        />
      </Tabs>

      {currentTab === 'business' && (
        <Stack spacing={3}>
          <Card variant="outlined">
            <CardHeader
              title={
                <Stack
                  direction="row"
                  alignItems="center"
                  gap={1}
                  typography="subtitle1"
                  fontWeight={600}
                >
                  <BusinessIcon fontSize="small" />
                  {t('settings.business.profileTitle')}
                </Stack>
              }
              subheader={t('settings.business.profileSubtitle')}
            />
            <CardContent>
              <Stack spacing={2}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <TextField
                    label={t('settings.business.businessName')}
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    data-testid="input-business-name"
                    fullWidth
                  />
                  <TextField
                    label={t('settings.business.urlSlug')}
                    value={formData.slug}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''),
                      })
                    }
                    data-testid="input-business-slug"
                    fullWidth
                    error={Boolean(slugReservedError)}
                    helperText={slugReservedError || t('settings.business.slugHelper')}
                  />
                </Stack>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <TextField
                    select
                    label={t('settings.business.businessType')}
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    data-testid="select-business-category"
                    fullWidth
                  >
                    {BUSINESS_TYPE_OPTIONS.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {t(`settings.businessTypes.${opt.value}`)}
                      </MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    select
                    label={t('settings.business.bookingMode')}
                    value={formData.bookingMode}
                    onChange={(e) => setFormData({ ...formData, bookingMode: e.target.value })}
                    data-testid="select-booking-mode"
                    fullWidth
                    helperText={t('settings.business.bookingModeHelper')}
                  >
                    {BOOKING_MODE_OPTIONS.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {t(`settings.bookingModes.${opt.value}`)}
                      </MenuItem>
                    ))}
                  </TextField>
                </Stack>
                <TextField
                  label={t('settings.business.phone')}
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder={t('settings.bookingPage.phonePlaceholder')}
                  data-testid="input-phone"
                  fullWidth
                  InputProps={{
                    startAdornment: (
                      <Phone sx={{ mr: 1, color: 'action.active' }} fontSize="small" />
                    ),
                  }}
                />
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <TextField
                    label={t('common.email')}
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    data-testid="input-email"
                    fullWidth
                    InputProps={{
                      startAdornment: (
                        <Email sx={{ mr: 1, color: 'action.active' }} fontSize="small" />
                      ),
                    }}
                  />
                  <TextField
                    label={t('common.website')}
                    value={formData.website}
                    onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                    data-testid="input-website"
                    fullWidth
                    InputProps={{
                      startAdornment: (
                        <Public sx={{ mr: 1, color: 'action.active' }} fontSize="small" />
                      ),
                    }}
                  />
                </Stack>
                <TextField
                  label={t('common.address')}
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  data-testid="input-address"
                  fullWidth
                  InputProps={{
                    startAdornment: (
                      <Place sx={{ mr: 1, color: 'action.active' }} fontSize="small" />
                    ),
                  }}
                />
                <TextField
                  label={t('common.description')}
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  data-testid="input-description"
                  multiline
                  minRows={3}
                  fullWidth
                />
              </Stack>
            </CardContent>
          </Card>

          <Card variant="outlined">
            <CardHeader
              title={
                <Stack
                  direction="row"
                  alignItems="center"
                  gap={1}
                  typography="subtitle1"
                  fontWeight={600}
                >
                  <LinkIcon fontSize="small" />
                  {t('settings.business.bookingLinkTitle')}
                </Stack>
              }
              subheader={t('settings.business.bookingLinkSubtitle')}
            />
            <CardContent>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                <TextField
                  value={bookingUrl}
                  InputProps={{ readOnly: true }}
                  size="small"
                  sx={{
                    flex: 1,
                    minWidth: 200,
                    '& input': { fontFamily: 'monospace', fontSize: 13 },
                  }}
                  data-testid="input-booking-url"
                />
                <IconButton
                  onClick={copyBookingUrl}
                  data-testid="button-copy-booking-url"
                  aria-label={String(t('settings.bookingPage.copyUrl'))}
                >
                  {urlCopied ? <Check color="success" /> : <ContentCopy />}
                </IconButton>
                <IconButton
                  component="a"
                  href={bookingUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="button-open-booking-url"
                  aria-label={String(t('settings.bookingPage.openBookingPage'))}
                >
                  <OpenInNew />
                </IconButton>
              </Stack>
            </CardContent>
          </Card>
        </Stack>
      )}

      {currentTab === 'booking-rules' && (
        <Stack spacing={3}>
          <Card variant="outlined">
            <CardHeader
              title={
                <Stack
                  direction="row"
                  alignItems="center"
                  gap={1}
                  typography="subtitle1"
                  fontWeight={600}
                >
                  <Public fontSize="small" />
                  {t('settings.rules.timezoneTitle')}
                </Stack>
              }
              subheader={t('settings.rules.timezoneSubtitle')}
            />
            <CardContent>
              <TextField
                select
                label={t('settings.rules.timezone')}
                value={formData.timezone}
                onChange={(e) => setFormData({ ...formData, timezone: e.target.value })}
                data-testid="select-timezone"
                fullWidth
              >
                {TIMEZONE_IDS.map((id) => (
                  <MenuItem key={id} value={id}>
                    {t(timezoneLabelKey(id))}
                  </MenuItem>
                ))}
              </TextField>
            </CardContent>
          </Card>
          <Card variant="outlined">
            <CardHeader
              title={
                <Stack
                  direction="row"
                  alignItems="center"
                  gap={1}
                  typography="subtitle1"
                  fontWeight={600}
                >
                  <Schedule fontSize="small" />
                  {t('settings.rules.availabilityTitle')}
                </Stack>
              }
              subheader={t('settings.rules.availabilitySubtitle')}
            />
            <CardContent>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                <TextField
                  label={t('settings.rules.minLeadTime')}
                  type="number"
                  value={formData.minLeadTimeMins}
                  onChange={(e) =>
                    setFormData({ ...formData, minLeadTimeMins: parseInt(e.target.value) || 0 })
                  }
                  data-testid="input-min-lead-time"
                  fullWidth
                  helperText={t('settings.rules.minLeadHelper')}
                  InputProps={{
                    endAdornment: <Typography variant="caption">{t('common.mins')}</Typography>,
                  }}
                />
                <TextField
                  label={t('settings.rules.maxBookingWindow')}
                  type="number"
                  value={formData.maxBookingDays}
                  onChange={(e) =>
                    setFormData({ ...formData, maxBookingDays: parseInt(e.target.value) || 0 })
                  }
                  data-testid="input-max-booking-days"
                  fullWidth
                  helperText={t('settings.rules.maxBookingHelper')}
                  InputProps={{
                    endAdornment: <Typography variant="caption">{t('common.days')}</Typography>,
                  }}
                />
                <TextField
                  select
                  label={t('settings.rules.slotIncrement')}
                  value={String(formData.slotIncrementMins)}
                  onChange={(e) =>
                    setFormData({ ...formData, slotIncrementMins: parseInt(e.target.value) })
                  }
                  data-testid="select-slot-increment"
                  fullWidth
                  helperText={t('settings.rules.slotIncrementHelper')}
                >
                  {(['5', '10', '15', '30', '60'] as const).map((m) => (
                    <MenuItem key={m} value={m}>
                      {t(`settings.rules.slotMinutes.${m}`)}
                    </MenuItem>
                  ))}
                </TextField>
              </Stack>
            </CardContent>
          </Card>
          <Card variant="outlined">
            <CardHeader
              title={
                <Stack
                  direction="row"
                  alignItems="center"
                  gap={1}
                  typography="subtitle1"
                  fontWeight={600}
                >
                  <Article fontSize="small" />
                  {t('settings.rules.cancellationTitle')}
                </Stack>
              }
              subheader={t('settings.rules.cancellationSubtitle')}
              subheaderTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
            />
            <CardContent>
              <TextField
                value={formData.cancellationPolicyText}
                onChange={(e) =>
                  setFormData({ ...formData, cancellationPolicyText: e.target.value })
                }
                data-testid="input-cancellation-policy"
                multiline
                minRows={4}
                fullWidth
                size="small"
                placeholder={t('settings.rules.cancellationPlaceholder')}
              />
            </CardContent>
          </Card>
        </Stack>
      )}

      {currentTab === 'working-hours' && (
        <Stack spacing={3}>
          <Card variant="outlined">
            <CardHeader
              title={
                <Stack
                  direction="row"
                  alignItems="center"
                  gap={1}
                  typography="subtitle1"
                  fontWeight={600}
                >
                  <CalendarMonth fontSize="small" />
                  {t('settings.hours.businessTitle')}
                </Stack>
              }
              subheader={t('settings.hours.businessSubtitle')}
            />
            <CardContent>
              <Stack divider={<Divider flexItem />}>
                {DAY_DISPLAY_ORDER.map((dayIndex) => {
                  const day = openingHours.find((d) => d.dayOfWeek === dayIndex) ?? {
                    dayOfWeek: dayIndex,
                    isOpen: false,
                    startTime: '09:00',
                    endTime: '17:00',
                  };
                  return (
                    <Stack
                      key={dayIndex}
                      direction="row"
                      alignItems="center"
                      spacing={2}
                      data-testid={`row-day-${dayIndex}`}
                      sx={{ py: 1, opacity: day.isOpen ? 1 : 0.65 }}
                    >
                      <Switch
                        checked={day.isOpen}
                        onChange={(_, c) => updateDayHours(dayIndex, { isOpen: c })}
                        data-testid={`switch-day-${dayIndex}`}
                      />
                      <Typography
                        sx={{ width: 100, flexShrink: 0 }}
                        variant="body2"
                        fontWeight={600}
                      >
                        {t(`daysOfWeek.${dayIndex}`)}
                      </Typography>
                      {day.isOpen ? (
                        <Stack direction="row" alignItems="center" spacing={1} flex={1}>
                          <TimeTextField
                            size="small"
                            value={day.startTime}
                            onChange={(e) =>
                              updateDayHours(dayIndex, { startTime: e.target.value })
                            }
                            data-testid={`input-open-time-${dayIndex}`}
                            InputLabelProps={{ shrink: true }}
                          />
                          <Typography variant="body2" color="text.secondary">
                            {t('common.to')}
                          </Typography>
                          <TimeTextField
                            size="small"
                            value={day.endTime}
                            onChange={(e) => updateDayHours(dayIndex, { endTime: e.target.value })}
                            data-testid={`input-close-time-${dayIndex}`}
                            InputLabelProps={{ shrink: true }}
                          />
                        </Stack>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          {t('common.closed')}
                        </Typography>
                      )}
                    </Stack>
                  );
                })}
              </Stack>
            </CardContent>
          </Card>

          <Card variant="outlined">
            <CardHeader
              title={
                <Stack
                  direction="row"
                  alignItems="center"
                  gap={1}
                  typography="subtitle1"
                  fontWeight={600}
                >
                  <People fontSize="small" />
                  {t('settings.hours.teamTitle')}
                </Stack>
              }
              subheader={t('settings.hours.teamSubtitle')}
            />
            <CardContent sx={{ pt: 0 }}>
              {!staffList || staffList.length === 0 ? (
                <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 3 }}>
                  {t('settings.hours.noStaff')}{' '}
                  <MuiLink component={Link} to="/admin/staff" underline="hover">
                    {t('settings.hours.staffPageLink')}
                  </MuiLink>
                  .
                </Typography>
              ) : (
                <Stack divider={<Divider flexItem />} sx={{ borderTop: 1, borderColor: 'divider' }}>
                  {staffList.map((member, idx) => {
                    const rowKey = staffScheduleRowKey(member, idx);
                    const placeholderStaff = isPlaceholderStaffId(member.id);
                    const hours = staffSchedules[rowKey] ?? [];
                    const expanded = expandedStaff.has(rowKey);
                    const activeDaySet = new Set(
                      hours.filter((h) => h.isActive).map((h) => h.dayOfWeek)
                    );
                    const summaryParts = DAY_DISPLAY_ORDER.filter((d) => activeDaySet.has(d)).map(
                      (d) => t(`daysOfWeekShort.${d}`)
                    );
                    const summary =
                      summaryParts.length === 0
                        ? t('settings.hours.noWorkingDays')
                        : summaryParts.join(', ');
                    return (
                      <Box key={rowKey} data-testid={`card-staff-schedule-${rowKey}`}>
                        <Stack
                          direction="row"
                          alignItems="center"
                          spacing={2}
                          sx={{ py: 2, cursor: 'pointer' }}
                          onClick={() => toggleExpand(rowKey)}
                          data-testid={`button-expand-staff-${rowKey}`}
                        >
                          <Avatar sx={{ width: 40, height: 40, fontSize: 14 }}>
                            {getInitials(member.displayName)}
                          </Avatar>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="body2" fontWeight={600}>
                              {member.displayName}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ display: 'block' }}
                            >
                              {summary}
                            </Typography>
                          </Box>
                          {expanded ? (
                            <ExpandMore color="action" />
                          ) : (
                            <ChevronRight color="action" />
                          )}
                        </Stack>
                        <Collapse in={expanded}>
                          <Box sx={{ px: 0, pb: 2, pt: 0 }}>
                            {DAY_DISPLAY_ORDER.map((dayIndex) => {
                              const day = hours.find((h) => h.dayOfWeek === dayIndex) ?? {
                                dayOfWeek: dayIndex,
                                isActive: false,
                                startTime: '09:00',
                                endTime: '17:00',
                              };
                              return (
                                <Stack
                                  key={dayIndex}
                                  direction="row"
                                  alignItems="center"
                                  spacing={2}
                                  data-testid={`row-staff-day-${member.id}-${dayIndex}`}
                                  sx={{
                                    py: 1,
                                    borderTop: 1,
                                    borderColor: 'divider',
                                    opacity: day.isActive ? 1 : 0.65,
                                  }}
                                >
                                  <Switch
                                    checked={day.isActive}
                                    onChange={(_, c) =>
                                      updateStaffDayHours(rowKey, dayIndex, { isActive: c })
                                    }
                                    data-testid={`switch-staff-day-${member.id}-${dayIndex}`}
                                  />
                                  <Typography sx={{ width: 100 }} variant="body2" fontWeight={600}>
                                    {t(`daysOfWeek.${dayIndex}`)}
                                  </Typography>
                                  {day.isActive ? (
                                    <Stack direction="row" alignItems="center" spacing={1} flex={1}>
                                      <TimeTextField
                                        size="small"
                                        value={day.startTime}
                                        onChange={(e) =>
                                          updateStaffDayHours(rowKey, dayIndex, {
                                            startTime: e.target.value,
                                          })
                                        }
                                        disabled={placeholderStaff}
                                        data-testid={`input-staff-open-${rowKey}-${dayIndex}`}
                                        InputLabelProps={{ shrink: true }}
                                      />
                                      <Typography variant="body2" color="text.secondary">
                                        {t('common.to')}
                                      </Typography>
                                      <TimeTextField
                                        size="small"
                                        value={day.endTime}
                                        onChange={(e) =>
                                          updateStaffDayHours(rowKey, dayIndex, {
                                            endTime: e.target.value,
                                          })
                                        }
                                        data-testid={`input-staff-close-${member.id}-${dayIndex}`}
                                        InputLabelProps={{ shrink: true }}
                                      />
                                    </Stack>
                                  ) : (
                                    <Typography variant="body2" color="text.secondary">
                                      {t('common.off')}
                                    </Typography>
                                  )}
                                </Stack>
                              );
                            })}
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                              <Button
                                size="small"
                                variant="contained"
                                startIcon={<Save />}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  saveStaffHours(rowKey, member.id);
                                }}
                                disabled={placeholderStaff || savingStaff[rowKey]}
                                data-testid={`button-save-staff-hours-${rowKey}`}
                              >
                                {savingStaff[rowKey]
                                  ? t('settings.hours.savingSchedule')
                                  : t('settings.hours.saveSchedule')}
                              </Button>
                            </Box>
                          </Box>
                        </Collapse>
                      </Box>
                    );
                  })}
                </Stack>
              )}
            </CardContent>
          </Card>
        </Stack>
      )}

      {currentTab === 'payments' && (
        <Stack spacing={3}>
          <Card variant="outlined">
            <CardHeader
              title={
                <Stack
                  direction="row"
                  alignItems="center"
                  gap={1}
                  typography="subtitle1"
                  fontWeight={600}
                >
                  <AttachMoney fontSize="small" />
                  {t('settings.payments.depositTitle')}
                </Stack>
              }
              subheader={t('settings.payments.depositSubtitle')}
            />
            <CardContent>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 2, mb: 2 }}
              >
                <Box>
                  <Typography fontWeight={600}>{t('settings.payments.requireDeposit')}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t('settings.payments.requireDepositHelper')}
                  </Typography>
                </Box>
                <Switch
                  checked={formData.depositEnabled}
                  onChange={(_, c) => setFormData({ ...formData, depositEnabled: c })}
                  data-testid="switch-deposit-enabled"
                />
              </Stack>
              {formData.depositEnabled && (
                <Box sx={{ pl: 2, borderLeft: 4, borderColor: 'primary.light' }}>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
                    <TextField
                      select
                      label={t('settings.payments.depositType')}
                      value={formData.defaultDepositType}
                      onChange={(e) =>
                        setFormData({ ...formData, defaultDepositType: e.target.value })
                      }
                      data-testid="select-deposit-type"
                      fullWidth
                    >
                      <MenuItem value="fixed">{t('settings.payments.fixedAmount')}</MenuItem>
                      <MenuItem value="percent">{t('settings.payments.percentOfPrice')}</MenuItem>
                    </TextField>
                    <TextField
                      label={
                        formData.defaultDepositType === 'percent'
                          ? t('settings.payments.percentAmount')
                          : formData.currency === 'GBP'
                          ? t('settings.payments.amountPence')
                          : t('settings.payments.amountCents')
                      }
                      type="number"
                      value={formData.defaultDepositValue}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          defaultDepositValue: parseInt(e.target.value) || 0,
                        })
                      }
                      data-testid="input-deposit-value"
                      fullWidth
                      inputProps={{
                        min: 0,
                        max: formData.defaultDepositType === 'percent' ? 100 : undefined,
                      }}
                    />
                  </Stack>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    {t('settings.payments.defaultDeposit', { preview: depositPreview })}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block">
                    {t('settings.payments.depositFooterNote')}
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Stack>
      )}

      {currentTab === 'notifications' && (
        <Stack spacing={3}>
          <Card variant="outlined">
            <CardHeader
              title={
                <Stack
                  direction="row"
                  alignItems="center"
                  gap={1}
                  typography="subtitle1"
                  fontWeight={600}
                >
                  <Notifications fontSize="small" />
                  {t('settings.notifications.adminTitle')}
                </Stack>
              }
              subheader={t('settings.notifications.adminSubtitle')}
            />
            <CardContent>
              <Stack spacing={1.5}>
                <NotifRow
                  label={t('settings.notifications.newBooking')}
                  description={t('settings.notifications.newBookingDesc')}
                  checked={notifPrefs.adminNewBooking}
                  onChange={(v) => setNotifPrefs({ ...notifPrefs, adminNewBooking: v })}
                  testId="switch-notif-admin-new-booking"
                />
                <NotifRow
                  label={t('settings.notifications.bookingCancelled')}
                  description={t('settings.notifications.bookingCancelledDesc')}
                  checked={notifPrefs.adminCancellation}
                  onChange={(v) => setNotifPrefs({ ...notifPrefs, adminCancellation: v })}
                  testId="switch-notif-admin-cancellation"
                />
                <NotifRow
                  label={t('settings.notifications.noShow')}
                  description={t('settings.notifications.noShowDesc')}
                  checked={notifPrefs.adminNoShow ?? false}
                  onChange={(v) => setNotifPrefs({ ...notifPrefs, adminNoShow: v })}
                  testId="switch-notif-admin-no-show"
                />
              </Stack>
            </CardContent>
          </Card>
          <Card variant="outlined">
            <CardHeader
              title={
                <Stack
                  direction="row"
                  alignItems="center"
                  gap={1}
                  typography="subtitle1"
                  fontWeight={600}
                >
                  <People fontSize="small" />
                  {t('settings.notifications.staffTitle')}
                </Stack>
              }
              subheader={t('settings.notifications.staffSubtitle')}
            />
            <CardContent>
              <NotifRow
                label={t('settings.notifications.assigned')}
                description={t('settings.notifications.assignedDesc')}
                checked={notifPrefs.staffNewBooking ?? true}
                onChange={(v) => setNotifPrefs({ ...notifPrefs, staffNewBooking: v })}
                testId="switch-notif-staff-new-booking"
              />
            </CardContent>
          </Card>
          <Card variant="outlined">
            <CardHeader
              title={
                <Stack
                  direction="row"
                  alignItems="center"
                  gap={1}
                  typography="subtitle1"
                  fontWeight={600}
                >
                  <MailOutline fontSize="small" />
                  {t('settings.notifications.customerTitle')}
                </Stack>
              }
              subheader={t('settings.notifications.customerSubtitle')}
            />
            <CardContent>
              <Stack spacing={1.5}>
                <NotifRow
                  label={t('settings.notifications.confirmation')}
                  description={t('settings.notifications.confirmationDesc')}
                  checked={notifPrefs.customerConfirmation}
                  onChange={(v) => setNotifPrefs({ ...notifPrefs, customerConfirmation: v })}
                  testId="switch-notif-customer-confirmation"
                />
                <NotifRow
                  label={t('settings.notifications.cancelConfirm')}
                  description={t('settings.notifications.cancelConfirmDesc')}
                  checked={notifPrefs.customerCancellation ?? true}
                  onChange={(v) => setNotifPrefs({ ...notifPrefs, customerCancellation: v })}
                  testId="switch-notif-customer-cancellation"
                />
                <Stack
                  sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 2 }}
                  spacing={1}
                >
                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                    <Box>
                      <Typography variant="body2" fontWeight={600}>
                        {t('settings.notifications.reminder')}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {t('settings.notifications.reminderDesc')}
                      </Typography>
                    </Box>
                    <Switch
                      checked={notifPrefs.customerReminder}
                      onChange={(_, v) => setNotifPrefs({ ...notifPrefs, customerReminder: v })}
                      data-testid="switch-notif-customer-reminder"
                    />
                  </Stack>
                  {notifPrefs.customerReminder && (
                    <TextField
                      select
                      size="small"
                      label={t('settings.notifications.send')}
                      value={String(notifPrefs.reminderLeadTimeHours ?? 24)}
                      onChange={(e) =>
                        setNotifPrefs({
                          ...notifPrefs,
                          reminderLeadTimeHours: parseInt(e.target.value),
                        })
                      }
                      data-testid="select-reminder-lead-time"
                      sx={{ maxWidth: 220 }}
                    >
                      {(['1', '2', '4', '12', '24', '48'] as const).map((h) => (
                        <MenuItem key={h} value={h}>
                          {t(`settings.reminderLead.${h}`)}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                </Stack>
                <NotifRow
                  label={t('settings.notifications.followUp')}
                  description={t('settings.notifications.followUpDesc')}
                  checked={notifPrefs.customerFollowUp ?? false}
                  onChange={(v) => setNotifPrefs({ ...notifPrefs, customerFollowUp: v })}
                  testId="switch-notif-customer-followup"
                />
              </Stack>
            </CardContent>
          </Card>
          <Typography variant="caption" color="text.secondary" align="center" display="block">
            {t('settings.notifications.footerNote')}
          </Typography>
        </Stack>
      )}

      {currentTab === 'booking-page' && (
        <Stack spacing={3}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            flexWrap="wrap"
            gap={2}
          >
            <Typography variant="body2" color="text.secondary">
              {t('settings.bookingPage.subtitle')}
            </Typography>
            <Stack direction="row" spacing={1}>
              <Button
                variant="outlined"
                startIcon={<Visibility />}
                onClick={() => window.open('/?preview=1', '_blank')}
                disabled={!hasBusiness}
                data-testid="button-preview-branding"
              >
                {t('common.preview')}
              </Button>
              <Button
                variant="outlined"
                startIcon={
                  publishBrandingMutation.isPending ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <Publish />
                  )
                }
                onClick={() => publishBrandingMutation.mutate()}
                disabled={
                  !hasBusiness ||
                  publishBrandingMutation.isPending ||
                  saveBrandingMutation.isPending
                }
                data-testid="button-publish-branding"
              >
                {publishBrandingMutation.isPending ? t('common.saving') : t('settings.bookingPage.publishLive')}
              </Button>
              <Button
                variant="contained"
                startIcon={
                  saveBrandingMutation.isPending ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <Save />
                  )
                }
                onClick={() => saveBrandingMutation.mutate()}
                disabled={
                  !hasBusiness ||
                  saveBrandingMutation.isPending ||
                  publishBrandingMutation.isPending
                }
                data-testid="button-save-branding"
              >
                {saveBrandingMutation.isPending ? t('common.saving') : t('common.save')}
              </Button>
            </Stack>
          </Stack>

          <Stack direction={{ xs: 'column', lg: 'row' }} spacing={3} alignItems="flex-start">
            <Stack spacing={3} sx={{ flex: 1, minWidth: 0, width: '100%' }}>
              <Card variant="outlined">
                <CardHeader
                  title={
                    <Stack direction="row" alignItems="center" gap={1}>
                      <Title fontSize="small" />
                      {t('settings.bookingPage.contentTitle')}
                    </Stack>
                  }
                  subheader={t('settings.bookingPage.contentSubtitle')}
                />
                <CardContent>
                  <Stack spacing={2}>
                    <TextField
                      label={t('settings.bookingPage.pageTitle')}
                      value={brandingCfg.pageTitle}
                      onChange={(e) => updateBrandingCfg({ pageTitle: e.target.value })}
                      data-testid="input-page-title"
                      fullWidth
                    />
                    <TextField
                      label={t('settings.bookingPage.tagline')}
                      value={brandingCfg.tagline || ''}
                      onChange={(e) => updateBrandingCfg({ tagline: e.target.value })}
                      data-testid="input-tagline"
                      fullWidth
                    />
                  </Stack>
                </CardContent>
              </Card>

              <Card variant="outlined">
                <CardHeader
                  title={
                    <Stack direction="row" alignItems="center" gap={1}>
                      <Palette fontSize="small" />
                      {t('settings.bookingPage.themeTitle')}
                    </Stack>
                  }
                  subheader={t('settings.bookingPage.themeSubtitle')}
                />
                <CardContent>
                  <Stack spacing={2}>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                      <TextField
                        select
                        label={t('settings.bookingPage.themeMode')}
                        value={brandingCfg.themeMode}
                        onChange={(e) =>
                          updateBrandingCfg({
                            themeMode: e.target.value as BookingPageConfig['themeMode'],
                          })
                        }
                        data-testid="select-theme-mode"
                        fullWidth
                      >
                        <MenuItem value="light">{t('settings.bookingPage.themeLight')}</MenuItem>
                        <MenuItem value="dark">{t('settings.bookingPage.themeDark')}</MenuItem>
                        <MenuItem value="auto">{t('settings.bookingPage.themeAuto')}</MenuItem>
                      </TextField>
                      <TextField
                        select
                        label={t('settings.bookingPage.cornerRadius')}
                        value={brandingCfg.radius}
                        onChange={(e) =>
                          updateBrandingCfg({
                            radius: e.target.value as BookingPageConfig['radius'],
                          })
                        }
                        data-testid="select-radius"
                        fullWidth
                      >
                        <MenuItem value="sm">{t('settings.bookingPage.radiusSm')}</MenuItem>
                        <MenuItem value="md">{t('settings.bookingPage.radiusMd')}</MenuItem>
                        <MenuItem value="lg">{t('settings.bookingPage.radiusLg')}</MenuItem>
                        <MenuItem value="xl">{t('settings.bookingPage.radiusXl')}</MenuItem>
                        <MenuItem value="pill">{t('settings.bookingPage.radiusPill')}</MenuItem>
                      </TextField>
                    </Stack>
                    <InputLabel shrink>{t('settings.bookingPage.primaryColor')}</InputLabel>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Box
                        component="input"
                        type="color"
                        value={brandingCfg.primaryColor}
                        onChange={(e) => updateBrandingCfg({ primaryColor: e.target.value })}
                        data-testid="input-primary-color-picker"
                        sx={{
                          width: 48,
                          height: 36,
                          border: 'none',
                          cursor: 'pointer',
                          bgcolor: 'transparent',
                        }}
                      />
                      <TextField
                        value={brandingCfg.primaryColor}
                        onChange={(e) => updateBrandingCfg({ primaryColor: e.target.value })}
                        data-testid="input-primary-color"
                        fullWidth
                        size="small"
                      />
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>

              <Card variant="outlined">
                <CardHeader
                  title={
                    <Stack direction="row" alignItems="center" gap={1}>
                      <Visibility fontSize="small" />
                      {t('settings.bookingPage.visibilityTitle')}
                    </Stack>
                  }
                  subheader={t('settings.bookingPage.visibilitySubtitle')}
                />
                <CardContent>
                  <Stack spacing={1}>
                    {brandingToggleItems.map(({ key, labelKey }) => (
                      <FormControlLabel
                        key={key}
                        labelPlacement="start"
                        sx={{ m: 0, justifyContent: 'space-between', width: '100%', ml: 0 }}
                        control={
                          <Switch
                            checked={!!brandingCfg[key]}
                            onChange={(_, c) => updateBrandingCfg({ [key]: c })}
                            data-testid={`switch-${String(key)}`}
                          />
                        }
                        label={t(labelKey)}
                      />
                    ))}
                  </Stack>
                </CardContent>
              </Card>
            </Stack>

            <Stack spacing={3} sx={{ flex: 1, minWidth: 0, width: '100%' }}>
              <Card variant="outlined" sx={{ width: '100%' }}>
                <CardHeader
                  title={t('settings.bookingPage.previewTitle')}
                  subheader={t('settings.bookingPage.previewSubtitle')}
                />
                <CardContent>
                  <Box
                    sx={{
                      border: 1,
                      borderColor: 'divider',
                      borderRadius: radiusToCss(brandingCfg.radius),
                      overflow: 'hidden',
                    }}
                  >
                    {brandingCfg.coverImageUrl && (
                      <Box
                        sx={{
                          height: 144,
                          backgroundImage: `url(${brandingCfg.coverImageUrl})`,
                          backgroundSize: 'cover',
                          backgroundPosition: 'center',
                        }}
                        data-testid="preview-cover"
                      />
                    )}
                    <Box sx={{ p: 2 }}>
                      <Stack direction="row" spacing={1.5} alignItems="center">
                        {brandingCfg.logoUrl && (
                          <Box
                            component="img"
                            src={brandingCfg.logoUrl}
                            alt={String(t('settings.bookingPage.logo'))}
                            sx={{ width: 40, height: 40, borderRadius: 1, objectFit: 'cover' }}
                            data-testid="preview-logo"
                          />
                        )}
                        <Box>
                          <Typography fontWeight={700} data-testid="preview-title">
                            {brandingCfg.pageTitle ||
                              t('settings.bookingPage.bookWithDefault', {
                                name: business?.name || t('settings.bookingPage.yourBusiness'),
                              })}
                          </Typography>
                          {brandingCfg.tagline && (
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              data-testid="preview-tagline"
                            >
                              {brandingCfg.tagline}
                            </Typography>
                          )}
                        </Box>
                      </Stack>
                      <Stack spacing={0.5} sx={{ mt: 2 }}>
                        {brandingCfg.showAddress && business?.address && (
                          <Typography variant="body2" color="text.secondary">
                            {business.address}
                          </Typography>
                        )}
                        {brandingCfg.showPhone && business?.phone && (
                          <Typography variant="body2" color="text.secondary">
                            {business.phone}
                          </Typography>
                        )}
                      </Stack>
                      <Box
                        sx={{
                          mt: 2,
                          border: 1,
                          borderColor: 'divider',
                          borderRadius: radiusToCss(brandingCfg.radius),
                          p: 1.5,
                        }}
                      >
                        <Stack direction="row" justifyContent="space-between">
                          <Typography variant="body2" fontWeight={600}>
                            {t('common.sampleService')}
                          </Typography>
                          <Stack direction="row" spacing={1}>
                            {brandingCfg.showDurations && (
                              <Typography variant="caption" color="text.secondary">
                                {t('bookingFlow.previewDuration')}
                              </Typography>
                            )}
                            {brandingCfg.showPrices && (
                              <Typography variant="body2" fontWeight={700}>
                                {t('bookingFlow.previewPrice')}
                              </Typography>
                            )}
                          </Stack>
                        </Stack>
                      </Box>
                      {brandingCfg.poweredByEposNow && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          display="block"
                          textAlign="center"
                          sx={{ mt: 2 }}
                        >
                          {t('common.poweredByEposNow')}
                        </Typography>
                      )}
                    </Box>
                    {(brandingCfg.galleryUrls || []).filter(Boolean).length > 0 && (
                      <Stack direction="row" spacing={1} sx={{ p: 2, pt: 0 }}>
                        {brandingCfg.galleryUrls
                          ?.filter(Boolean)
                          .slice(0, GALLERY_SLOT_COUNT)
                          .map((url, i) => (
                            <Box
                              component="img"
                              key={i}
                              src={url}
                              alt=""
                              sx={{ width: '50%', height: 80, objectFit: 'cover', borderRadius: 1 }}
                            />
                          ))}
                      </Stack>
                    )}
                  </Box>
                  <Stack
                    direction="row"
                    alignItems="center"
                    flexWrap="wrap"
                    columnGap={1}
                    rowGap={0.5}
                    sx={{ mt: 2 }}
                  >
                    <Stack direction="row" alignItems="center" gap={0.75}>
                      <Box
                        sx={{
                          width: 12,
                          height: 12,
                          borderRadius: 0.5,
                          bgcolor: brandingCfg.primaryColor,
                          border: 1,
                          borderColor: 'divider',
                          flexShrink: 0,
                        }}
                        aria-hidden
                      />
                      <Typography variant="caption" color="text.secondary" component="span">
                        {t('settings.bookingPage.previewMetaPrimary', {
                          color: brandingCfg.primaryColor,
                        })}
                      </Typography>
                    </Stack>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      component="span"
                      aria-hidden
                    >
                      |
                    </Typography>
                    <Typography variant="caption" color="text.secondary" component="span">
                      {t('settings.bookingPage.previewMetaRadius', { radius: brandingCfg.radius })}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      component="span"
                      aria-hidden
                    >
                      |
                    </Typography>
                    <Typography variant="caption" color="text.secondary" component="span">
                      {t('settings.bookingPage.previewMetaTheme', {
                        theme: bookingPageThemeModeLabel,
                      })}
                    </Typography>
                  </Stack>
                </CardContent>
              </Card>

              <Card variant="outlined">
                <CardHeader
                  title={
                    <Stack direction="row" alignItems="center" gap={1}>
                      <ImageIcon fontSize="small" />
                      {t('settings.bookingPage.imagesTitle')}
                    </Stack>
                  }
                  subheader={t('settings.bookingPage.imagesSubtitle')}
                />
                <CardContent>
                  <Stack spacing={3}>
                    <Box>
                      <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                        {t('settings.bookingPage.logo')}
                      </Typography>
                      <ImageUpload
                        value={brandingCfg.logoUrl || ''}
                        onChange={(url) => updateBrandingCfg({ logoUrl: url })}
                        testId="logo"
                        aspectRatio="square"
                        placeholder={t('settings.bookingPage.uploadLogoPlaceholder')}
                      />
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                        {t('settings.bookingPage.coverImage')}
                      </Typography>
                      <ImageUpload
                        value={brandingCfg.coverImageUrl || ''}
                        onChange={(url) => updateBrandingCfg({ coverImageUrl: url })}
                        testId="cover"
                        aspectRatio="wide"
                        placeholder={t('settings.bookingPage.uploadCoverPlaceholder')}
                      />
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                        {t('settings.bookingPage.gallery')}
                      </Typography>
                      <Box
                        sx={{
                          display: 'grid',
                          gridTemplateColumns: {
                            xs: '1fr',
                            sm: 'repeat(2, 1fr)',
                            md: 'repeat(3, 1fr)',
                          },
                          gap: 1.5,
                          alignItems: 'stretch',
                        }}
                      >
                        {Array.from({ length: GALLERY_SLOT_COUNT }, (_, i) => (
                          <ImageUpload
                            key={i}
                            value={gallerySlots[i] || ''}
                            onChange={(newUrl) => {
                              const next = [...gallerySlots];
                              next[i] = newUrl;
                              updateBrandingCfg({ galleryUrls: next });
                            }}
                            testId={`gallery-${i}`}
                            aspectRatio="gallery"
                            placeholder={t('settings.bookingPage.galleryPhotoPlaceholder', {
                              n: i + 1,
                            })}
                          />
                        ))}
                      </Box>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Stack>
          </Stack>
        </Stack>
      )}
    </Box>
  );
}
